import { Inject, Injectable } from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from "@angular/common/http";
import { AsyncSubject, forkJoin, Observable, throwError, timer } from 'rxjs';
import { ConfigurationService } from './configuration.service';
import { DataPathUtils } from '../utils/dataPath.utils';
import {UrlUtils} from '../utils/url.utils';
import { catchError, flatMap } from 'rxjs/operators';
import {AuthenticationService} from './authentication.service';
import apigClientFactory, { ApiGatewayClient } from 'aws-api-gateway-client';
import { config, CognitoIdentityCredentials } from "aws-sdk";
import { CommonUtils } from 'app/utils/common.utils';
import {ToastrService} from "ngx-toastr";
import {GlobalConstants} from "../common/global-constants";

@Injectable()
export class RequestsService {
  private errorMessageDataPaths: string[] = [];
  private unauthorizedRedirectUrl: string;
  private defaultRegion: string;
  private defaultArc: string;
  private jwtToken: string;
	private apiEndpoints: any;
  private initialized$ = new AsyncSubject();
  private apiEndpointRegions: any;
  private cognitoAuthRegion: string;
  private identityPoolId: string;
  private userPoolId: string;
  private credentialsReady$ = new AsyncSubject<boolean>();

  constructor(
    private readonly http: HttpClient,
    @Inject('UrlUtils') private readonly urlUtils: UrlUtils,
    @Inject('DataPathUtils') private readonly dataPathUtils: DataPathUtils,
    @Inject('ConfigurationService') private readonly configurationService: ConfigurationService,
    @Inject('AuthenticationService') private readonly authenticationService: AuthenticationService,
    @Inject('CommonUtils') private readonly commonUtils: CommonUtils,
    private toastrService: ToastrService,
  ) {
    forkJoin(
      configurationService.getSettings(),
      authenticationService.getAuthenticated()
    ).subscribe(([settings, jwtToken]) => {
      this.apiEndpoints = settings.apiEndpoints;
      this.defaultRegion = settings.defaultRegion;
      this.defaultArc = settings.defaultArc;

      this.jwtToken = jwtToken;
      this.identityPoolId = settings.identityPoolId;
      this.userPoolId = settings.cognitoAuth.UserPoolId;
      this.apiEndpointRegions = settings.apiEndpointRegions;
      this.cognitoAuthRegion = settings.awsIdentityPoolIdRegion;
      this.getCognitoIdentityCredentials();
      this.initialized$.next(undefined);
      this.initialized$.complete();
    });
  }

  public get(url, headers = null, queryParams = null) {
    const additionalParams = {
      headers: headers
    }
    return this.invokeApiGateway(queryParams, url, 'GET', additionalParams, {});
  }

  public post(url, data, headers = null) {
    const additionalParams = {
      headers: headers
    }
    return this.invokeApiGateway({}, url, 'POST', additionalParams, data);
  }

  public put(url, data, headers = null) {
    const additionalParams = {
      headers: headers
    }
    return this.invokeApiGateway({}, url, 'PUT', additionalParams, data);
  }

  private invokeApiGateway(pathParams, pathTemplate, method, additionalParams, body) {
    return this.initialized$.pipe(
      flatMap(() => this.credentialsReady$),
      flatMap(() => {
        const apiGatewayClient = this.getAPIGatewayClient();
        return new Observable((observer) => {
          apiGatewayClient.invokeApi(pathParams, pathTemplate, method, additionalParams, body).then((result) => {
            observer.next(result['data'])
          }).catch((error) => {
            observer.error(new Error(error.toString()))
            if ((<AWS.CognitoIdentityCredentials>config.credentials).needsRefresh()) {
              this.toastrService.error(GlobalConstants.REFRESH_SESSION_REQUIRED, GlobalConstants.TITLE_ERROR);
              timer(GlobalConstants.PAGE_RELOAD_INTERVAL).subscribe(()=>{
                window.location.reload()
              })
            }
          });
        });
      })
    );
  }

  public putFileToS3Bucket(url, data, headers = null) {
    return this.executeRequestAndHandleErrors(() =>
      this.http.put(url, data,{
        reportProgress: true,
        observe: 'events'
      }));
  }

  public delete(url, headers = null) {
    return this.executeRequestAndHandleErrors(() =>
      this.http.delete(this.baseUrl() + url, { headers: this.buildHeaders(headers) }));
  }

  public patch(url, data, headers = null) {
    return this.executeRequestAndHandleErrors(() =>
      this.http.patch(this.baseUrl() + url, data, { headers: this.buildHeaders(headers) }));
  }

  private executeRequestAndHandleErrors(invokeRequest: () => Observable<any>): Observable<{}> {
    return this.initialized$.pipe(flatMap(() => invokeRequest().pipe(catchError(error => this.handleError(error)))));
  }

  private buildHeaders(heads) {
    let headers = new HttpHeaders();

    if (heads) {
      for (const head in heads) {
        if (heads.hasOwnProperty(head)) {
          headers = headers.append(head, heads[head]);
        }
      }
    }
    return headers;
  }

  private getAPIGatewayClient() {
    const client = apigClientFactory.newClient({
      accessKey: config.credentials.accessKeyId,
      invokeUrl: this.baseUrl(),
      region: this.getApiEndpointRegion(),
      sessionToken: config.credentials.sessionToken,
      secretKey: config.credentials.secretAccessKey,
    });
    return client;
  }

  private extractData(res: Response) {
    try {
      let body = res.json();
      return body || { };
    } catch (e) {
      return res;
    }
  }

  private buildUrl(url, queryParams) {
    if (!queryParams || !queryParams.length) {
      return url;
    }

    let outputUrl = url;
    const params = [];

    for (let param of queryParams) {
      if (param.name) {
        let urlParamName = `:${param.name}`;
        if (outputUrl.indexOf(urlParamName) >= 0){
          outputUrl = outputUrl.replace(urlParamName, param.value);
        } else if (!param.urlReplaceOnly) {
          params.push(`${param.name}=${param.value || ''}`);
        }
      }
    }

    if (params.length) {
      const firstSeparator = url.indexOf('?') >= 0 ? '&' : '?';
      return outputUrl + firstSeparator + params.join('&');
    }
    return outputUrl;
  }

  private baseUrl() {
    const region = this.urlUtils.getSearchParam(location.href, 'region', this.defaultRegion);
    const arc = this.urlUtils.getSearchParam(location.href, 'arc', this.defaultArc);
    console.log(`Region for the request is: ${region}`);
    return this.apiEndpoints[arc][region];
  }

  private getApiEndpointRegion() {
    const region = this.commonUtils.getRegion();
    const arc = this.commonUtils.getArc();
    console.log(`Region for the request is: ${region}`);
    return this.apiEndpointRegions[arc][region];
  }

  private handleError(error: HttpErrorResponse | any) {
    console.error(error);
    return throwError(error.message ? error.message : error.toString());
  }

  private getCognitoIdentityCredentials() {
    const creds = new CognitoIdentityCredentials({
      IdentityPoolId: this.identityPoolId,
      Logins: {
        ['cognito-idp.' + this.cognitoAuthRegion + '.amazonaws.com/' + this.userPoolId]: this.jwtToken
      }
    });

    config.update({
      region: 'us-east-1',
      credentials: creds,
    });

    // Return a promise that resolves when credentials are ready
    config.getCredentials((err) => {
      if (err) {
        console.error("Error getting AWS credentials:", err);
        this.credentialsReady$.error(err);
      } else {
        console.log("AWS credentials successfully loaded");
        this.credentialsReady$.next(true);
        this.credentialsReady$.complete();
      }
    });
  }
}
